import {useEffect} from "react";
import {useLocation, useNavigate} from "react-router-dom";
import {setAuthentication, setLoggedInUserDetail, useRayzayUIController} from "../../context";
import {clearAllAuthData, loginByRefreshToken} from "../../api/auth/authApiClient";
import {debugTokenStatus, getLoggedInUserDetails, isTokenExpiring, isTokenValid} from "../jwt/jwtUtil";
import {getAllUserByUserName} from "../../api/user/userApiClient";
import {checkBackendHealth} from "../../api/health/healthApiClient";
import axiosApiClient from "../../api/axios";

function useAuthGuard() {
    const refreshTokenName = process.env.REACT_APP_REFRESH_TOKEN_KEY || 'refreshToken';
    const accessTokenName = process.env.REACT_APP_AUTH_TOKEN_KEY || 'token';
    const [controller, dispatch] = useRayzayUIController();
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        const unprotectedRoutes = [
            "/authentication/sign-in",
            "/authentication/sign-up",
            "/authentication/reset-password",
            "/authentication/set-new-password",
            "/maintenance"
        ];
        let isMounted = true;

        const checkBackendAndAuthenticate = async () => {
            try {
                // Debug token status for troubleshooting
                debugTokenStatus();

                // First, check if backend is alive (this doesn't require authentication)
                const healthResult = await checkBackendHealth();

                if (!healthResult.isAlive) {
                    if (!isMounted) return;
                    console.log("Backend is down, redirecting to maintenance page");
                    // Don't clear auth data when backend is down - tokens might still be valid
                    navigate("/maintenance");
                    return;
                }

                console.log("Backend is alive, proceeding with authentication");

                // Backend is alive, proceed with authentication
                const refreshToken = localStorage.getItem(refreshTokenName);
                const accessToken = localStorage.getItem(accessTokenName);

                // If no tokens exist and we're not on an unprotected route, redirect to signin
                if (!accessToken && !refreshToken) {
                    if (!isMounted) return;
                    if (!unprotectedRoutes.includes(location.pathname)) {
                        console.log("No tokens found, redirecting to sign-in");
                        clearAllAuthData();
                        setAuthentication(dispatch, false);
                        setLoggedInUserDetail(dispatch, null);
                        navigate("/authentication/sign-in");
                    }
                    return;
                }

                // Check if we have a valid token locally
                if (accessToken && isTokenValid(accessToken)) {
                    console.log("Current token is valid locally, validating with backend");

                    // Validate token with backend using profile endpoint
                    try {
                        const response = await axiosApiClient.get('/api/auth/profile');
                        if (response.data && response.data.username) {
                            console.log("Token validated successfully with backend");

                            // If token is expiring soon, try to refresh it proactively
                            if (isTokenExpiring(accessToken)) {
                                console.log("Token is expiring soon, attempting proactive refresh");
                                if (refreshToken) {
                                    try {
                                        const response = await loginByRefreshToken();
                                        if (!isMounted) return;
                                        console.log("Token refreshed proactively");
                                        setAuthentication(dispatch, true);

                                        // Get user details from the new token
                                        const decoded = getLoggedInUserDetails(response.token);
                                        if (decoded && decoded.username) {
                                            try {
                                                const userDetail = await getAllUserByUserName(decoded.username);
                                                if (!isMounted) return;
                                                setLoggedInUserDetail(dispatch, userDetail.data);
                                            } catch (userError) {
                                                console.error("Error fetching user details:", userError);
                                            }
                                        }
                                    } catch (err) {
                                        console.error("Proactive token refresh failed:", err);
                                        if (!isMounted) return;

                                        // Check if it's a network error vs authentication error
                                        if (err.response && err.response.status === 401) {
                                            console.log("Authentication error during proactive refresh, clearing auth data");
                                            clearAllAuthData();
                                            setAuthentication(dispatch, false);
                                            setLoggedInUserDetail(dispatch, null);
                                            if (!unprotectedRoutes.includes(location.pathname)) {
                                                navigate("/authentication/sign-in");
                                            }
                                        } else {
                                            console.log("Network error during proactive refresh, keeping auth data");
                                            // Don't clear auth data for network errors
                                        }
                                    }
                                } else {
                                    // No refresh token available
                                    if (!isMounted) return;
                                    console.log("Token expiring but no refresh token available");
                                    clearAllAuthData();
                                    setAuthentication(dispatch, false);
                                    setLoggedInUserDetail(dispatch, null);
                                    if (!unprotectedRoutes.includes(location.pathname)) {
                                        navigate("/authentication/sign-in");
                                    }
                                }
                            } else {
                                // Token is valid and not expiring soon
                                if (!controller.isAuthenticated) {
                                    setAuthentication(dispatch, true);

                                    // Get user details if we don't have them
                                    const decoded = getLoggedInUserDetails(accessToken);
                                    if (decoded && decoded.username && !controller.loggedInUserDetail) {
                                        try {
                                            const userDetail = await getAllUserByUserName(decoded.username);
                                            if (!isMounted) return;
                                            setLoggedInUserDetail(dispatch, userDetail.data);
                                        } catch (userError) {
                                            console.error("Error fetching user details:", userError);
                                        }
                                    }
                                }
                            }
                        }
                    } catch (error) {
                        console.error("Token validation with backend failed:", error);
                        if (error.response?.status === 401) {
                            // Token is invalid on backend, try to refresh
                            if (refreshToken) {
                                try {
                                    const response = await loginByRefreshToken();
                                    if (!isMounted) return;
                                    console.log("Token refreshed successfully after backend validation failed");
                                    setAuthentication(dispatch, true);

                                    // Get user details from the new token
                                    const decoded = getLoggedInUserDetails(response.token);
                                    if (decoded && decoded.username) {
                                        try {
                                            const userDetail = await getAllUserByUserName(decoded.username);
                                            if (!isMounted) return;
                                            setLoggedInUserDetail(dispatch, userDetail.data);
                                        } catch (userError) {
                                            console.error("Error fetching user details:", userError);
                                        }
                                    }
                                } catch (refreshError) {
                                    if (!isMounted) return;
                                    console.error("Token refresh failed:", refreshError);
                                    clearAllAuthData();
                                    setAuthentication(dispatch, false);
                                    setLoggedInUserDetail(dispatch, null);
                                    if (!unprotectedRoutes.includes(location.pathname)) {
                                        navigate("/authentication/sign-in");
                                    }
                                }
                            } else {
                                if (!isMounted) return;
                                console.log("Invalid token and no refresh token available");
                                clearAllAuthData();
                                setAuthentication(dispatch, false);
                                setLoggedInUserDetail(dispatch, null);
                                if (!unprotectedRoutes.includes(location.pathname)) {
                                    navigate("/authentication/sign-in");
                                }
                            }
                        } else if (error.response?.status === 403) {
                            // 403 errors might be resource-specific, not authentication failures
                            // Don't clear auth data or redirect for 403 errors
                            console.log("403 error during token validation - this might be resource-specific");
                        } else {
                            console.log("Network error during token validation, keeping auth data");
                            // Don't clear auth data for network errors
                        }
                    }
                } else {
                    // No valid token, try to refresh if we have a refresh token
                    if (refreshToken) {
                        try {
                            console.log("No valid token, attempting to refresh");
                            const response = await loginByRefreshToken();
                            if (!isMounted) return;

                            setAuthentication(dispatch, true);
                            console.log("Token refreshed successfully");

                            // Get user details from the new token
                            const decoded = getLoggedInUserDetails(response.token);
                            if (decoded && decoded.username) {
                                try {
                                    const userDetail = await getAllUserByUserName(decoded.username);
                                    if (!isMounted) return;
                                    setLoggedInUserDetail(dispatch, userDetail.data);
                                } catch (userError) {
                                    console.error("Error fetching user details:", userError);
                                }
                            }
                        } catch (err) {
                            if (!isMounted) return;
                            console.error("Error refreshing token:", err);

                            // Only clear auth data if it's an actual authentication failure
                            if (err.response && err.response.status === 401) {
                                console.log("Authentication error during token refresh, clearing auth data");
                                clearAllAuthData();
                                setAuthentication(dispatch, false);
                                setLoggedInUserDetail(dispatch, null);
                                if (!unprotectedRoutes.includes(location.pathname)) {
                                    navigate("/authentication/sign-in");
                                }
                            } else {
                                console.log("Network error during token refresh, keeping auth data");
                                // Don't clear auth data for network errors
                            }
                        }
                    } else {
                        if (!isMounted) return;
                        console.log("No refresh token available, redirecting to login");
                        // Clear any stale auth data
                        clearAllAuthData();
                        setAuthentication(dispatch, false);
                        setLoggedInUserDetail(dispatch, null);
                        if (!unprotectedRoutes.includes(location.pathname)) {
                            navigate("/authentication/sign-in");
                        }
                    }
                }
            } catch (error) {
                console.error("Backend health check failed:", error);
                if (!isMounted) return;
                navigate("/maintenance");
            }
        };

        // Only authenticate if not on unprotected routes
        if (!unprotectedRoutes.includes(location.pathname)) {
            // If user is already authenticated and has user details, skip the check
            if (controller.isAuthenticated && controller.loggedInUserDetail) {
                console.log("User already authenticated, skipping auth check");
                return;
            }
            checkBackendAndAuthenticate();
        }

        // Set up more frequent checks (every 2 minutes instead of 5)
        const intervalId = setInterval(() => {
            if (controller.isAuthenticated && !unprotectedRoutes.includes(location.pathname)) {
                checkBackendAndAuthenticate();
            }
        }, 2 * 60 * 1000); // 2 minutes

        return () => {
            isMounted = false;
            clearInterval(intervalId);
        };
    }, [location.pathname, navigate, dispatch, accessTokenName, refreshTokenName, controller.isAuthenticated, controller.loggedInUserDetail]);
}

export default useAuthGuard;
